import React, { useState, useEffect, useRef } from 'react';
import { Camera, CheckCircle2, XCircle, Loader2, ScanLine } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AhorroCifra } from '../ui/AhorroCifra';
import { Html5Qrcode } from 'html5-qrcode';
import { cajaService } from '../../services/caja';
import { toast } from '../../hooks/use-toast';

/*
  El escáner de la caja.

  Viene de `ScanQR.jsx`, la pantalla pública con desplegable de tiendas, y el
  cambio que lo justifica es lo que ya NO tiene: no se elige tienda. La tienda
  es la del enlace por el que entró la caja y la confirma el servidor, así que
  aquí desaparecieron el selector y su validación. Lo demás —la captura de los
  dos montos, la sugerencia del descuento, la entrada manual del código— se
  conserva tal cual porque ya estaba probado en caja.

  Formato: BRAND.md. La pantalla de trabajo va sobre **lienzo claro**, no sobre
  el degradado, y es una decisión del manual, no una preferencia: la regla
  60/30/10 dice que el color es acento y nunca satura pantallas completas. El
  degradado se reserva para la puerta y para el momento de celebrar el ahorro
  —que son instantes— mientras que esto es la herramienta que el cajero mira
  cincuenta veces al día bajo la luz del mostrador.

  `onSesionInvalida` se dispara cuando el servidor responde que la sesión
  terminó (turno vencido o PIN rotado). El padre vuelve a pedir el PIN sin
  perder lo que el cajero llevaba escrito en pantalla.
*/

/* Tarjeta blanca del sistema: radio de card, línea suave y sombra cálida
   (BRAND.md §4: nunca sombras duras ni grises frías). */
const Tarjeta = ({ className = '', children }) => (
  <div className={`bg-white border border-vd-linea rounded-card shadow-warm-sm ${className}`}>{children}</div>
);

const Etiqueta = ({ htmlFor, children }) => (
  <Label htmlFor={htmlFor} className="text-vd-label uppercase text-vd-pizarra block mb-2">
    {children}
  </Label>
);

const campoClases =
  'h-12 rounded-input border-vd-linea bg-white text-vd-tinta placeholder:text-vd-pizarra/70 shadow-none';

const pesos = (n) => `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 2 })}`;

const CajaScanner = ({ slug, storeName, onSesionInvalida }) => {
  const [scanning, setScanning] = useState(false);
  const [scannedToken, setScannedToken] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [selectedBenefitId, setSelectedBenefitId] = useState(null);
  const [scannerName, setScannerName] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  /* Módulo 6: los dos montos de los que sale el ahorro del cliente. */
  const [ticketTotal, setTicketTotal] = useState('');
  /* Código escrito a mano: respaldo cuando la cámara no sirve. */
  const [codigoManual, setCodigoManual] = useState('');
  /* El descuento ya no es un campo: lo calcula el servidor a partir del
     beneficio dado de alta. Aquí solo se enseña lo que se va a aplicar. */
  /*
    La foto del ticket (Iván, 13-ago-2026).

    El número de ticket lo teclea el cajero, así que no prueba nada: repite lo
    que alguien escribió. La foto sí es evidencia de la venta tal como quedó
    registrada, y es la que sirve el día que una marca discuta un descuento.

    Se guardan dos cosas: el archivo que se manda y una URL local solo para la
    vista previa, que hay que revocar al soltarla — si no, cada foto tomada se
    queda en memoria toda la jornada, y una caja no se recarga en horas.
  */
  const [fotoTicket, setFotoTicket] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  /* Al aplicar se guarda lo que se le ahorró al cliente, porque el resumen se
     pinta después de limpiar los campos. */
  const [aplicado, setAplicado] = useState(null);

  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    return () => {
      // Limpiar scanner al desmontar
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  /* Una sesión caída no es un error del canje: es tener que volver a abrir la
     caja. Se distingue aquí para no enseñarle al cajero un mensaje técnico. */
  const esSesionCaida = (err) => err?.status === 401 || err?.data?.reason_code === 'CAJA_SESION_INVALIDA';

  const checkCameraPermissions = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('Este navegador no puede abrir la cámara. Usa Chrome, Firefox, Safari o Edge.');
    }

    const isSecureContext = window.isSecureContext ||
      window.location.protocol === 'https:' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    if (!isSecureContext) {
      throw new Error('Para usar la cámara hace falta una conexión segura (https).');
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (permissionError) {
      if (permissionError.name === 'NotAllowedError' || permissionError.name === 'PermissionDeniedError') {
        throw new Error('No nos diste permiso para la cámara. Actívalo en el navegador y vuelve a intentar.');
      } else if (permissionError.name === 'NotFoundError' || permissionError.name === 'DevicesNotFoundError') {
        throw new Error('No encontramos ninguna cámara en este equipo.');
      } else if (permissionError.name === 'NotReadableError' || permissionError.name === 'TrackStartError') {
        throw new Error('Otra aplicación está usando la cámara. Ciérrala e intenta de nuevo.');
      }
      throw new Error(`No pudimos abrir la cámara: ${permissionError.message}`);
    }
  };

  const startScanning = async () => {
    try {
      setScanning(true);
      setError(null);
      setScannedToken(null);
      setBenefits([]);
      setSelectedBenefitId(null);
      setAplicado(null);

      await checkCameraPermissions();

      const html5QrCode = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = html5QrCode;

      const configuracion = { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 };
      const alLeer = (decodedText) => handleScannedCode(decodedText);
      const alFallar = (errorMessage) => {
        // Los fallos de lectura continua no son errores: el escáner reporta
        // uno por cuadro hasta que encuentra el código.
        if (errorMessage && !errorMessage.includes('NotFoundException')) {
          console.debug('QR scan error:', errorMessage);
        }
      };

      try {
        await html5QrCode.start({ facingMode: 'environment' }, configuracion, alLeer, alFallar);
      } catch (startError) {
        // Sin cámara trasera (una laptop en el mostrador), se intenta la frontal.
        console.warn('Error con cámara trasera, intentando con cámara frontal:', startError);
        try {
          await html5QrCode.stop();
        } catch (stopError) {
          // Ignorar error al detener
        }
        await html5QrCode.start({ facingMode: 'user' }, configuracion, alLeer, alFallar);
      }
    } catch (err) {
      console.error('Error iniciando escáner:', err);
      setScanning(false);
      const errorMessage = err.message || 'No pudimos abrir la cámara.';
      setError(errorMessage);
      toast({ title: 'La cámara no abrió', description: errorMessage, variant: 'destructive' });
    }
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error('Error deteniendo escáner:', err);
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const handleScannedCode = async (token) => {
    await stopScanning();

    setScannedToken(token);
    setLoading(true);
    setError(null);
    setBenefits([]);
    setSelectedBenefitId(null);

    try {
      const response = await cajaService.validarQr(slug, token);

      if (response.success && response.status === 'valid') {
        setBenefits(response.data.benefits || []);

        if (response.data.benefits && response.data.benefits.length === 0) {
          setError(`Este cliente no tiene beneficios disponibles en ${storeName}.`);
        }
      } else {
        setError(response.message || 'Ese código no es válido.');
      }
    } catch (err) {
      console.error('Error validando QR:', err);

      if (esSesionCaida(err)) {
        onSesionInvalida?.();
        return;
      }

      setError(err.message || 'No pudimos leer ese código.');
    } finally {
      setLoading(false);
    }
  };

  /*
    Lo que se le va a aplicar al cliente, para enseñárselo al cajero.

    Es una VISTA PREVIA, no el dato que se guarda: la cifra que cuenta la
    calcula el servidor en `Benefit::ahorroPara()` y vuelve en la respuesta del
    canje. Aquí se repite el mismo criterio solo para que el cajero vea qué va a
    pasar antes de confirmar.

    El campo editable que había aquí se quitó (Ricardo, 9-sep-2026). Existía
    porque «el descuento real del ticket manda, que casi siempre hay artículos
    excluidos o topes», pero en todo el histórico eso ocurrió una sola vez —y
    resultó ser un dedazo—, mientras que el campo sí permitió once canjes
    guardados con céntimos. El descuento que aplica es el que está dado de alta,
    y punto.
  */
  const beneficioElegido = benefits.find((b) => b.id === selectedBenefitId);
  const totalNum = Number(ticketTotal);

  const vistaPrevia = (() => {
    if (!beneficioElegido) return null;

    const pct = Number(beneficioElegido.discount_percentage);
    if (Number.isFinite(pct) && pct > 0 && pct <= 100) {
      return { modo: 'porcentaje', pct, monto: totalNum > 0 ? Math.round(totalNum * pct) / 100 : 0 };
    }

    const fijo = Number(beneficioElegido.perk_value);
    if (Number.isFinite(fijo) && fijo > 0) {
      return { modo: 'valor_fijo', monto: totalNum > 0 ? Math.min(fijo, totalNum) : fijo };
    }

    return {
      modo: 'en_especie',
      monto: 0,
      etiqueta: beneficioElegido.perk_label || beneficioElegido.title,
    };
  })();

  const puedeAplicar =
    Boolean(ticketNumber.trim()) &&
    Boolean(scannerName.trim()) &&
    totalNum > 0;

  const handleRedeem = async () => {
    if (!selectedBenefitId) {
      toast({ title: 'Falta el beneficio', description: 'Elige cuál le vas a aplicar.', variant: 'destructive' });
      return;
    }

    if (!(Number(ticketTotal) > 0)) {
      toast({ title: 'Falta el total', description: 'Escribe cuánto pagó el cliente.', variant: 'destructive' });
      return;
    }

    if (!ticketNumber.trim() || !scannerName.trim()) {
      toast({ title: 'Faltan datos', description: 'El número de ticket y tu nombre son obligatorios.', variant: 'destructive' });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await cajaService.canjear(slug, {
        token: scannedToken,
        benefitId: selectedBenefitId,
        ticketNumber: ticketNumber.trim(),
        scannerName: scannerName.trim(),
        ticketTotal: Number(ticketTotal),
        fotoTicket,
      });

      if (response.success) {
        /* El ahorro que se enseña es el que devolvió el servidor, no el que
           esta pantalla hubiera calculado: lo que se celebra tiene que ser
           exactamente lo que quedó guardado. */
        const ahorro = response.data?.ahorro ?? null;
        setAplicado({
          ahorro: ahorro?.monto ?? 0,
          modoAhorro: ahorro?.modo ?? null,
          etiquetaAhorro: ahorro?.etiqueta ?? null,
          ticket: ticketNumber.trim(),
          // La miniatura de la confirmación sale de la copia local, no del
          // servidor: se ve al instante y no depende de que la imagen ya esté
          // servible. Se conserva hasta pasar al siguiente cliente.
          foto: fotoPreview,
        });
        limpiarVenta({ conservarFoto: true });
      } else {
        throw new Error(response.error || 'No se pudo aplicar el beneficio.');
      }
    } catch (err) {
      console.error('Error redimiendo QR:', err);

      if (esSesionCaida(err)) {
        onSesionInvalida?.();
        return;
      }

      setError(err.message || 'No se pudo aplicar el beneficio.');
      toast({ title: 'No se aplicó', description: err.message || 'Intenta de nuevo.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  /* Limpia la venta en curso. `scannerName` NO se toca a propósito: es la misma
     persona toda la jornada y reescribirlo en cada cliente es un impuesto
     absurdo. `aplicado` tampoco, porque es lo que se está mostrando. */
  const limpiarVenta = ({ conservarFoto = false } = {}) => {
    setScannedToken(null);
    setBenefits([]);
    setSelectedBenefitId(null);
    setTicketNumber('');
    setTicketTotal('');
    setCodigoManual('');
    setFotoTicket(null);
    /* Tras un canje la vista previa sigue viva un momento más: es la miniatura
       que se enseña en la confirmación. Se suelta al pasar al siguiente. */
    if (!conservarFoto) {
      soltarFoto();
    }
    setError(null);
    setLoading(false);
  };

  /* Libera la URL local de la vista previa. Sin esto, cada foto tomada se
     queda en memoria hasta que se recargue la página — y una caja pasa horas
     abierta. */
  const soltarFoto = () => {
    setFotoPreview((anterior) => {
      if (anterior) URL.revokeObjectURL(anterior);
      return null;
    });
  };

  const elegirFoto = (archivo) => {
    if (!archivo) return;
    soltarFoto();
    setFotoTicket(archivo);
    setFotoPreview(URL.createObjectURL(archivo));
  };

  const cancelar = () => {
    limpiarVenta();
    setAplicado(null);
  };

  const siguienteCliente = () => {
    soltarFoto();
    cancelar();
    startScanning();
  };

  /* ── Confirmación: se le ahorró tanto ──
     La cifra va en degradado rojo→rosa porque BRAND.md §1 lo pide para toda
     cifra de ahorro. Y aquí además hace trabajo: es lo que el cajero le puede
     decir al cliente en voz alta. */
  if (aplicado) {
    return (
      <Tarjeta className="p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-vd-exito/10 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-7 h-7 text-vd-exito" />
        </div>

        <h2 className="font-display text-vd-h2 text-vd-tinta">¡Listo!</h2>

        {/* Un beneficio en especie no se celebra con una cifra: se dice qué se
            llevó. «Le ahorraste $0» sería mentira dos veces. */}
        {aplicado.modoAhorro === 'en_especie' ? (
          <>
            <p className="text-vd-body text-vd-pizarra mt-2">Se lleva</p>
            <p className="font-display text-vd-h2 text-vd-tinta mt-1 leading-snug">
              {aplicado.etiquetaAhorro}
            </p>
          </>
        ) : (
          <>
            <p className="text-vd-body text-vd-pizarra mt-2">Le ahorraste</p>
            <AhorroCifra as="div" className="mt-1" style={{ fontSize: 'clamp(40px, 12vw, 56px)' }}>
              {pesos(aplicado.ahorro)}
            </AhorroCifra>
          </>
        )}

        {/*
          La evidencia, no el folio.

          Iván (13-ago-2026): «FOTO DE TICKET, NO FOLIO». Antes esta línea
          presumía «ticket 12345» — un número que había tecleado el propio
          cajero treinta segundos antes, así que confirmaba únicamente que
          sabía escribir. La foto sí dice algo: que la venta existió como quedó
          registrada.

          Cuando no hay foto se sigue enseñando el número: es lo único que hay,
          y callarlo dejaría la confirmación sin ninguna referencia de la venta.
        */}
        {aplicado.foto ? (
          <div className="mt-5">
            <img
              src={aplicado.foto}
              alt={`Ticket ${aplicado.ticket} de la compra`}
              className="mx-auto rounded-card border border-vd-linea"
              style={{ maxHeight: 220, objectFit: 'contain' }}
            />
            <p className="text-sm text-vd-pizarra mt-3">
              Quedó registrado en {storeName} · ticket {aplicado.ticket}
            </p>
          </div>
        ) : (
          <p className="text-sm text-vd-pizarra mt-4">
            Quedó registrado en {storeName} · ticket {aplicado.ticket}
          </p>
        )}

        <Button variant="vondi" size="pill" onClick={siguienteCliente} className="w-full mt-7">
          Atender al siguiente
        </Button>
      </Tarjeta>
    );
  }

  return (
    <div className="space-y-4">
      {/* Nombre de quien atiende: sigue siendo persona, no tienda. La tienda
          ya la sabe el enlace; esto es para saber quién aplicó el descuento. */}
      {!scannedToken && !benefits.length && (
        <Tarjeta className="p-6">
          <Etiqueta htmlFor="scanner-name">¿Quién está atendiendo?</Etiqueta>
          <Input
            id="scanner-name"
            type="text"
            value={scannerName}
            onChange={(e) => setScannerName(e.target.value)}
            placeholder="Tu nombre y caja"
            className={campoClases}
            disabled={scanning || loading}
          />
          <p className="text-xs text-vd-pizarra mt-2">Se guarda con cada venta. Lo escribes una vez por turno.</p>
        </Tarjeta>
      )}

      {/* Área de escaneo */}
      {!scannedToken && !benefits.length && (
        <Tarjeta className="p-6 space-y-4">
          {!scanning ? (
            <div className="space-y-4">
              <div className="aspect-square rounded-card bg-vd-niebla border border-dashed border-vd-linea flex flex-col items-center justify-center gap-3">
                <ScanLine className="w-12 h-12 text-vd-naranja" strokeWidth={1.5} />
                <p className="text-sm text-vd-pizarra px-8 text-center">
                  Pídele su código y apunta la cámara
                </p>
              </div>

              {error && (
                <div className="rounded-input bg-vd-alerta/8 border border-vd-alerta/25 px-4 py-3">
                  <p className="text-sm text-vd-alerta">{error}</p>
                </div>
              )}

              <Button
                variant="vondi"
                size="pill"
                onClick={startScanning}
                disabled={!scannerName.trim() || loading}
                className="w-full"
              >
                <Camera className="w-4 h-4" />
                Escanear el código
              </Button>

              {/*
                Respaldo por si la cámara falla.

                En la caja pasa: el permiso no se dio, la lente está sucia, la
                pantalla del cliente está rota o con demasiado brillo. Sin esta
                salida, el cajero se queda sin poder aplicar el descuento y el
                cliente se va sin su beneficio.
              */}
              <div className="pt-4 border-t border-vd-linea space-y-2">
                <Etiqueta htmlFor="codigo-manual">¿La cámara no jala? Escribe el código</Etiqueta>
                <div className="flex gap-2">
                  <Input
                    id="codigo-manual"
                    value={codigoManual}
                    onChange={(e) => setCodigoManual(e.target.value.trim())}
                    placeholder="Código del cliente"
                    className={campoClases}
                    disabled={loading}
                  />
                  <Button
                    variant="vondiSecundario"
                    size="pill"
                    onClick={() => handleScannedCode(codigoManual)}
                    disabled={!codigoManual || !scannerName.trim() || loading}
                  >
                    Buscar
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* El visor sí va oscuro: es video, y el marco claro alrededor
                  hace que el encuadre se lea mejor. */}
              <div id="qr-reader" className="aspect-square rounded-card overflow-hidden bg-vd-tinta"></div>
              <Button variant="vondiSecundario" size="pill" onClick={stopScanning} className="w-full">
                Detener
              </Button>
            </div>
          )}
        </Tarjeta>
      )}

      {/* Validando */}
      {loading && !benefits.length && (
        <Tarjeta className="p-8 text-center">
          <Loader2 className="w-7 h-7 text-vd-naranja animate-spin mx-auto mb-3" />
          <p className="text-vd-body text-vd-pizarra">Revisando el código...</p>
        </Tarjeta>
      )}

      {/* Código rechazado */}
      {error && !benefits.length && !scanning && scannedToken && (
        <Tarjeta className="p-6">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-vd-alerta shrink-0 mt-0.5" />
            <p className="text-vd-body text-vd-tinta">{error}</p>
          </div>
          <Button variant="vondi" size="pill" onClick={siguienteCliente} className="w-full mt-5">
            Intentar otra vez
          </Button>
        </Tarjeta>
      )}

      {/* Beneficios disponibles del cliente */}
      {benefits.length > 0 && (
        <Tarjeta className="p-6 space-y-5">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-vd-exito" />
            <h3 className="font-display text-vd-h3 text-vd-tinta">Código válido</h3>
          </div>

          <div className="space-y-3">
            <Etiqueta>¿Cuál le aplicas?</Etiqueta>
            {benefits.map((benefit) => {
              const elegido = selectedBenefitId === benefit.id;
              return (
                <button
                  type="button"
                  key={benefit.id}
                  onClick={() => setSelectedBenefitId(benefit.id)}
                  className={`w-full text-left p-4 rounded-card border transition-all ${
                    elegido
                      ? 'border-vd-naranja bg-vd-niebla shadow-warm-sm'
                      : 'border-vd-linea bg-white hover:border-vd-pizarra/40'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0 ${
                        elegido ? 'border-vd-naranja' : 'border-vd-linea'
                      }`}
                    >
                      {elegido && <div className="w-2.5 h-2.5 rounded-full bg-grad-vivo" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-vd-tinta">{benefit.title}</p>
                      {benefit.description && (
                        <p className="text-sm text-vd-pizarra mt-1 leading-snug">{benefit.description}</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedBenefitId && (
            <>
              {/*
                Captura del cajero. Son tres datos y en este orden a propósito:
                el total viene primero porque es el que tiene a la vista en su
                pantalla de venta, y el descuento se le calcula solo con el
                porcentaje del beneficio para que casi siempre solo confirme.
              */}
              <div className="pt-5 border-t border-vd-linea space-y-4">
                <div>
                  <Etiqueta htmlFor="total">Total de la compra</Etiqueta>
                  <Input
                    id="total"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={ticketTotal}
                    onChange={(e) => setTicketTotal(e.target.value)}
                    placeholder="$0.00"
                    className={`${campoClases} text-lg font-semibold`}
                    disabled={loading}
                  />
                </div>

                {/*
                  El descuento se enseña, no se escribe. Es el que está dado de
                  alta en el beneficio y el cajero no lo puede tocar.
                */}
                {vistaPrevia && (
                  <div className="rounded-input border border-vd-linea bg-vd-niebla px-4 py-3">
                    {vistaPrevia.modo === 'en_especie' ? (
                      <>
                        <div className="text-vd-label uppercase text-vd-pizarra mb-1">Se lleva</div>
                        <div className="text-lg font-semibold text-vd-tinta leading-snug">
                          {vistaPrevia.etiqueta}
                        </div>
                        <p className="text-xs text-vd-pizarra mt-1">
                          No es un descuento en pesos, así que no suma al ahorro del cliente.
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="text-vd-label uppercase text-vd-pizarra mb-1">
                          Descuento{vistaPrevia.modo === 'porcentaje' ? ` · ${vistaPrevia.pct}%` : ''}
                        </div>
                        <div className="text-2xl font-semibold text-vd-tinta">
                          {totalNum > 0 || vistaPrevia.modo === 'valor_fijo'
                            ? pesos(vistaPrevia.monto)
                            : '—'}
                        </div>
                        {totalNum <= 0 && vistaPrevia.modo === 'porcentaje' && (
                          <p className="text-xs text-vd-pizarra mt-1">Escribe el total para verlo.</p>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div>
                  <Etiqueta htmlFor="ticket">Número de ticket</Etiqueta>
                  <Input
                    id="ticket"
                    type="text"
                    value={ticketNumber}
                    onChange={(e) => setTicketNumber(e.target.value)}
                    placeholder="El folio del ticket"
                    className={campoClases}
                    disabled={loading}
                  />
                </div>

                {/*
                  La foto del ticket.

                  `capture="environment"` hace que en un teléfono se abra la
                  cámara trasera directamente, sin pasar por el selector de
                  archivos: en una caja con fila detrás, cada toque de más
                  cuenta. En un escritorio ese atributo se ignora y queda un
                  selector normal, que es lo correcto ahí.

                  Es opcional a propósito. Si fuera obligatoria, una cámara sin
                  permiso o un teléfono viejo dejarían al cliente sin su
                  descuento delante de todo el mundo.
                */}
                <div>
                  <Etiqueta htmlFor="foto-ticket">Foto del ticket</Etiqueta>

                  {fotoPreview ? (
                    <div className="rounded-card border border-vd-linea p-3 bg-white">
                      <img
                        src={fotoPreview}
                        alt="Vista previa del ticket"
                        className="w-full rounded-input"
                        style={{ maxHeight: 200, objectFit: 'contain' }}
                      />
                      <div className="flex gap-2 mt-3">
                        <label
                          htmlFor="foto-ticket"
                          className="flex-1 h-10 rounded-full border border-vd-linea text-vd-tinta text-sm font-medium flex items-center justify-center cursor-pointer"
                        >
                          Repetir la foto
                        </label>
                        <button
                          type="button"
                          onClick={soltarFoto}
                          disabled={loading}
                          className="h-10 px-4 rounded-full border border-vd-linea text-vd-pizarra text-sm"
                        >
                          Quitar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label
                      htmlFor="foto-ticket"
                      className="h-12 rounded-input border border-dashed border-vd-linea bg-white text-vd-pizarra flex items-center justify-center gap-2 cursor-pointer text-sm"
                    >
                      <Camera className="w-4 h-4" />
                      Tomar foto del ticket
                    </label>
                  )}

                  <input
                    id="foto-ticket"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    disabled={loading}
                    onChange={(e) => {
                      elegirFoto(e.target.files?.[0]);
                      // Se limpia para que elegir DOS VECES la misma foto
                      // vuelva a disparar el evento; si no, repetir la toma con
                      // el mismo archivo no haría nada.
                      e.target.value = '';
                    }}
                  />

                  <p className="text-xs text-vd-pizarra mt-2">
                    Opcional, pero es la prueba de la venta. Sin ella solo queda el número escrito a mano.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="vondi"
                  size="pill"
                  onClick={handleRedeem}
                  disabled={!puedeAplicar || loading}
                  className="flex-1"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Aplicando...
                    </>
                  ) : (
                    'Aplicar beneficio'
                  )}
                </Button>
                <Button variant="vondiSecundario" size="pill" onClick={cancelar} disabled={loading}>
                  Cancelar
                </Button>
              </div>
            </>
          )}
        </Tarjeta>
      )}
    </div>
  );
};

export default CajaScanner;
